import React, { useState, useEffect, useCallback } from 'react';
import { getVisitStats, getUserActionStats, getLoginActivityStats } from '../services/visitService';
import { useAuth } from '../context/AuthContext';

const VisitAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [allVisits, setAllVisits] = useState([]); // Store all loaded visits
  const [hasMoreVisits, setHasMoreVisits] = useState(true); // Whether more visits exist
  const [loadingMoreVisits, setLoadingMoreVisits] = useState(false); // Loading state for "Load More"
  
  const [adminStats, setAdminStats] = useState(null);
  const [adminVisits, setAdminVisits] = useState([]);
  const [allAdminVisits, setAllAdminVisits] = useState([]); // Store all loaded admin visits
  const [hasMoreAdminVisits, setHasMoreAdminVisits] = useState(true); // Whether more admin visits exist
  const [loadingMoreAdminVisits, setLoadingMoreAdminVisits] = useState(false); // Loading state for admin "Load More"
  
  const [actionStats, setActionStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [allActions, setAllActions] = useState([]); // Store all loaded actions
  const [actionsPage, setActionsPage] = useState(1); // Current page for actions
  const [hasMoreActions, setHasMoreActions] = useState(true); // Whether more actions exist
  const [loadingMoreActions, setLoadingMoreActions] = useState(false); // Loading state for actions "Load More"
  
  const [loginStats, setLoginStats] = useState(null);
  const [logins, setLogins] = useState([]);
  const [allLogins, setAllLogins] = useState([]); // Store all loaded logins
  const [loginsPage, setLoginsPage] = useState(1); // Current page for logins
  const [hasMoreLogins, setHasMoreLogins] = useState(true); // Whether more logins exist
  const [loadingMoreLogins, setLoadingMoreLogins] = useState(false); // Loading state for logins "Load More"
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('visits'); // 'visits', 'admin-visits', 'actions', or 'logins'
  const [environmentFilter, setEnvironmentFilter] = useState('live'); // 'all', 'live', 'test' - default to 'live'
  const [signupFilter, setSignupFilter] = useState('all'); // 'all', 'signups-only', 'abandoned-only'
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    limit: 50,
    page: '',
    actionType: '',
    loginMethod: '',
    userId: ''
  });

  const { firebaseUser } = useAuth();
  
  // Note: Admin checks are handled by AdminRoute wrapper in App.js
  // This component will only render if user is authenticated AND is admin

  const fetchStats = useCallback(async () => {
    console.log('🔄 fetchStats: Starting analytics fetch...');
    
    if (!firebaseUser) {
      console.log('❌ fetchStats: No firebaseUser found');
      setError('Authentication required');
      setLoading(false);
      return;
    }

    console.log('👤 fetchStats: User authenticated:', {
      uid: firebaseUser.uid,
      email: firebaseUser.email
    });

    try {
      setLoading(true);
      setError(null);
      
      const token = await firebaseUser.getIdToken();
      console.log('🎫 fetchStats: Got auth token, length:', token?.length);
      
      const filterOptions = {};
      
      if (filters.startDate) filterOptions.startDate = filters.startDate;
      if (filters.endDate) filterOptions.endDate = filters.endDate;
      if (filters.limit) filterOptions.limit = filters.limit;
      if (filters.pageType) filterOptions.pageType = filters.pageType; // Updated to use pageType

      console.log('📋 fetchStats: Filter options:', filterOptions);
      console.log('🌍 fetchStats: Environment filter:', environmentFilter);

      // Fetch visit, action, and login data
      console.log('📡 fetchStats: Making API calls...');
      const publicOptions = { 
        ...filterOptions, 
        pageType: 'public',
        environment: environmentFilter !== 'all' ? environmentFilter : undefined
      };
      const adminOptions = { 
        ...filterOptions, 
        pageType: 'admin',
        environment: environmentFilter !== 'all' ? environmentFilter : undefined
      };
      console.log('🔍 FRONTEND: Public query options:', publicOptions);
      console.log('🔍 FRONTEND: Admin query options:', adminOptions);
      
      const [visitData, adminVisitData, actionData, loginData] = await Promise.all([
        getVisitStats(publicOptions, token),
        getVisitStats(adminOptions, token),
        getUserActionStats({
          ...filterOptions,
          actionType: filters.actionType || undefined,
          environment: environmentFilter !== 'all' ? environmentFilter : undefined
        }, token),
        getLoginActivityStats({
          ...filterOptions,
          loginMethod: filters.loginMethod || undefined,
          userId: filters.userId || undefined
        }, token)
      ]);
      
      console.log('✅ fetchStats: API calls completed');
      console.log('📊 fetchStats: Visit data received:', {
        publicVisits: visitData?.visits?.length || 0,
        publicPagination: visitData?.pagination,
        publicStats: visitData?.stats
      });
      console.log('🔐 fetchStats: Admin visit data received:', {
        adminVisits: adminVisitData?.visits?.length || 0,
        adminPagination: adminVisitData?.pagination,
        adminStats: adminVisitData?.stats
      });
      console.log('🎬 fetchStats: Action data received:', {
        actions: actionData?.actions?.length || 0,
        rawActionsLength: actionData?.actions?.length
      });
      console.log('🔑 fetchStats: Login data received:', {
        logins: loginData?.activities?.length || 0
      });
      
      // First, set raw admin and action data
      const rawAdminVisits = adminVisitData.visits;
      const rawActions = actionData.actions;
      const rawLogins = loginData.activities;
      
      // Store raw count for logins BEFORE filtering for pagination logic (logins still need this until backend is updated)
      const rawLoginsCount = rawLogins?.length || 0;

      // Filter out admin pages from regular visits (no longer needed since we use pageType filter)
      const publicVisits = visitData.visits; // These are already filtered to public only
      
      // Apply environment filter to logins only (visits and actions are filtered by backend)
      const applyEnvironmentFilter = (items) => {
        if (environmentFilter === 'all') return items;
        if (environmentFilter === 'live') return items.filter(item => item.environment === 'live' || !item.isTest);
        if (environmentFilter === 'test') return items.filter(item => item.environment === 'test' || item.isTest);
        return items;
      };
      
      // Apply signup filter to actions
      const applySignupFilter = (items) => {
        if (signupFilter === 'all') return items;
        if (signupFilter === 'signups-only') {
          return items.filter(item => 
            item.actionType === 'signup_start' || 
            item.actionType === 'signup_complete' || 
            item.actionType === 'signup_abandon'
          );
        }
        if (signupFilter === 'abandoned-only') {
          return items.filter(item => item.actionType === 'signup_abandon');
        }
        return items;
      };
      
      // Visits and actions are already filtered by backend - no client-side environment filtering needed
      const filteredPublicVisits = publicVisits;
      const filteredAdminVisits = rawAdminVisits;
      // Actions are filtered by backend, but still apply signup filter client-side
      let filteredActions = applySignupFilter(rawActions);
      // Logins still need client-side filtering until backend is updated
      const filteredLogins = applyEnvironmentFilter(rawLogins);

      
      // Calculate stats based on filtered data (not backend stats, since we filter client-side)
      const publicStats = {
        totalVisits: filteredPublicVisits.length,
        authenticatedVisits: filteredPublicVisits.filter(v => v.isAuthenticated).length,
        uniqueUsers: new Set(filteredPublicVisits.filter(v => v.userId).map(v => v.userId)).size,
        pageViews: {},
        visitsByHour: {}
      };
      
      // Calculate page views for public pages
      filteredPublicVisits.forEach(visit => {
        const page = visit.page || '/';
        publicStats.pageViews[page] = (publicStats.pageViews[page] || 0) + 1;
        
        // Visits by hour
        if (visit.timestamp) {
          const hour = new Date(visit.timestamp).getHours();
          publicStats.visitsByHour[hour] = (publicStats.visitsByHour[hour] || 0) + 1;
        }
      });
      
      // Calculate admin stats based on filtered data (not backend stats, since we filter client-side)
      const calculatedAdminStats = {
        totalVisits: filteredAdminVisits.length,
        authenticatedVisits: filteredAdminVisits.filter(v => v.isAuthenticated).length,
        uniqueUsers: new Set(filteredAdminVisits.filter(v => v.userId).map(v => v.userId)).size,
        pageViews: {},
        visitsByHour: {}
      };
      
      filteredAdminVisits.forEach(visit => {
        const page = visit.page || '/';
        calculatedAdminStats.pageViews[page] = (calculatedAdminStats.pageViews[page] || 0) + 1;
        
        if (visit.timestamp) {
          const hour = new Date(visit.timestamp).getHours();
          calculatedAdminStats.visitsByHour[hour] = (calculatedAdminStats.visitsByHour[hour] || 0) + 1;
        }
      });
      
      // Recalculate action stats
      const calculatedActionStats = {
        totalActions: filteredActions.length,
        joinClicks: filteredActions.filter(a => a.actionType === 'join_click').length,
        signupStarts: filteredActions.filter(a => a.actionType === 'signup_start').length,
        signupCompletes: filteredActions.filter(a => a.actionType === 'signup_complete').length,
        signupAbandons: filteredActions.filter(a => a.actionType === 'signup_abandon').length
      };
      
      // Recalculate login stats
      const calculatedLoginStats = {
        totalLogins: filteredLogins.length,
        successfulLogins: filteredLogins.filter(l => l.success !== false).length,
        failedLogins: filteredLogins.filter(l => l.success === false).length,
        uniqueUsers: new Set(filteredLogins.map(l => l.userId)).size
      };
      
      // NOW set the filtered stats and visits
      setStats(publicStats);
      setVisits(filteredPublicVisits);
      setAllVisits(filteredPublicVisits); // Store all loaded visits
      setHasMoreVisits(visitData.pagination?.hasMore || false); // Use backend pagination info
      
      setAdminStats(calculatedAdminStats);
      setAdminVisits(filteredAdminVisits);
      setAllAdminVisits(filteredAdminVisits); // Store all loaded admin visits
      setHasMoreAdminVisits(adminVisitData.pagination?.hasMore || false); // Use backend pagination info
      
      setActionStats(calculatedActionStats);
      setActions(filteredActions);
      setAllActions(filteredActions); // Store all loaded actions
      setHasMoreActions(actionData.pagination?.hasMore || false); // Use backend pagination info
      
      setLoginStats(calculatedLoginStats);
      setLogins(filteredLogins);
      setAllLogins(filteredLogins); // Store all loaded logins
      // Check if we got the full limit from backend (not filtered count)
      setHasMoreLogins(rawLoginsCount >= filters.limit);
    } catch (err) {
      console.error('💥 fetchStats: Error occurred:', err);
      console.error('💥 fetchStats: Error message:', err.message);
      console.error('💥 fetchStats: Error stack:', err.stack);
      setError(err.message);
      console.error('Error fetching analytics:', err);
    } finally {
      console.log('🏁 fetchStats: Completed (finally block)');
      setLoading(false);
    }
  }, [firebaseUser, environmentFilter, signupFilter, filters]); // Dependencies for useCallback

  const loadMoreVisits = async () => {
    if (!firebaseUser || loadingMoreVisits || !hasMoreVisits) return;

    try {
      setLoadingMoreVisits(true);
      const token = await firebaseUser.getIdToken();
      
      const filterOptions = {
        limit: filters.limit,
        pageType: 'public', // Get public pages only
        environment: environmentFilter !== 'all' ? environmentFilter : undefined,
        lastVisitId: visits.length > 0 ? visits[visits.length - 1].id : null // Use cursor pagination
      };
      
      if (filters.startDate) filterOptions.startDate = filters.startDate;
      if (filters.endDate) filterOptions.endDate = filters.endDate;

      const visitData = await getVisitStats(filterOptions, token);
      const newVisits = visitData.visits || [];
      
      // No need for client-side environment filtering - backend already filtered
      // Append new visits to existing ones
      const updatedAllVisits = [...allVisits, ...newVisits];
      setAllVisits(updatedAllVisits);
      setVisits(updatedAllVisits);
      
      // Update stats based on all loaded visits
      setStats(prevStats => ({
        ...prevStats,
        totalVisits: updatedAllVisits.length,
        authenticatedVisits: updatedAllVisits.filter(v => v.isAuthenticated).length,
        uniqueUsers: new Set(updatedAllVisits.filter(v => v.userId).map(v => v.userId)).size
      }));
      
      // Update pagination status based on backend response
      setHasMoreVisits(visitData.pagination?.hasMore || false);
    } catch (err) {
      console.error('Error loading more visits:', err);
      setError(err.message);
    } finally {
      setLoadingMoreVisits(false);
    }
  };

  const loadMoreAdminVisits = async () => {
    if (!firebaseUser || loadingMoreAdminVisits || !hasMoreAdminVisits) return;

    try {
      setLoadingMoreAdminVisits(true);
      const token = await firebaseUser.getIdToken();
      
      const filterOptions = {
        limit: filters.limit,
        pageType: 'admin', // Get admin pages only
        environment: environmentFilter !== 'all' ? environmentFilter : undefined,
        lastVisitId: adminVisits.length > 0 ? adminVisits[adminVisits.length - 1].id : null // Use cursor pagination
      };
      
      if (filters.startDate) filterOptions.startDate = filters.startDate;
      if (filters.endDate) filterOptions.endDate = filters.endDate;

      const adminVisitData = await getVisitStats(filterOptions, token);
      
      // These should already be admin pages only
      const newAdminVisits = adminVisitData.visits;
      
      // No need for client-side environment filtering - backend already filtered
      // Append new admin visits to existing ones
      const updatedAllAdminVisits = [...allAdminVisits, ...newAdminVisits];
      setAllAdminVisits(updatedAllAdminVisits);
      setAdminVisits(updatedAllAdminVisits);
      
      // Update admin stats based on all loaded visits
      setAdminStats(prevStats => ({
        ...prevStats,
        totalVisits: updatedAllAdminVisits.length,
        authenticatedVisits: updatedAllAdminVisits.filter(v => v.isAuthenticated).length,
        uniqueUsers: new Set(updatedAllAdminVisits.filter(v => v.userId).map(v => v.userId)).size
      }));
      
      // Update pagination status based on backend response
      setHasMoreAdminVisits(adminVisitData.pagination?.hasMore || false);
    } catch (err) {
      console.error('Error loading more admin visits:', err);
      setError(err.message);
    } finally {
      setLoadingMoreAdminVisits(false);
    }
  };

  const loadMoreActions = async () => {
    if (!firebaseUser || loadingMoreActions || !hasMoreActions) return;

    try {
      setLoadingMoreActions(true);
      const token = await firebaseUser.getIdToken();
      
      const filterOptions = {
        limit: filters.limit,
        actionType: filters.actionType || undefined,
        environment: environmentFilter !== 'all' ? environmentFilter : undefined,
        lastActionId: actions.length > 0 ? actions[actions.length - 1].id : null
      };
      
      if (filters.startDate) filterOptions.startDate = filters.startDate;
      if (filters.endDate) filterOptions.endDate = filters.endDate;

      const actionData = await getUserActionStats(filterOptions, token);
      
      const newActions = actionData.actions;
      
      // Apply signup filter (environment already filtered by backend)
      const applySignupFilter = (items) => {
        if (signupFilter === 'all') return items;
        if (signupFilter === 'signups-only') {
          return items.filter(item => 
            item.actionType === 'signup_start' || 
            item.actionType === 'signup_complete' || 
            item.actionType === 'signup_abandon'
          );
        }
        if (signupFilter === 'abandoned-only') {
          return items.filter(item => item.actionType === 'signup_abandon');
        }
        return items;
      };
      
      const filteredNewActions = applySignupFilter(newActions);
      
      // Append new actions to existing ones
      const updatedAllActions = [...allActions, ...filteredNewActions];
      setAllActions(updatedAllActions);
      setActions(updatedAllActions);
      setActionsPage(actionsPage + 1);
      // Use backend pagination info
      setHasMoreActions(actionData.pagination?.hasMore || false);
    } catch (err) {
      console.error('Error loading more actions:', err);
      setError(err.message);
    } finally {
      setLoadingMoreActions(false);
    }
  };

  const loadMoreLogins = async () => {
    if (!firebaseUser || loadingMoreLogins || !hasMoreLogins) return;

    try {
      setLoadingMoreLogins(true);
      const token = await firebaseUser.getIdToken();
      
      const filterOptions = {
        limit: filters.limit,
        loginMethod: filters.loginMethod || undefined,
        userId: filters.userId || undefined
      };
      
      if (filters.startDate) filterOptions.startDate = filters.startDate;
      if (filters.endDate) filterOptions.endDate = filters.endDate;

      const loginData = await getLoginActivityStats(filterOptions, token);
      
      const newLogins = loginData.activities;
      const rawNewLoginsCount = newLogins?.length || 0;
      
      // Apply environment filter
      const applyEnvironmentFilter = (items) => {
        if (environmentFilter === 'all') return items;
        if (environmentFilter === 'live') return items.filter(item => item.environment === 'live' || !item.isTest);
        if (environmentFilter === 'test') return items.filter(item => item.environment === 'test' || item.isTest);
        return items;
      };
      
      const filteredNewLogins = applyEnvironmentFilter(newLogins);
      
      // Append new logins to existing ones
      const updatedAllLogins = [...allLogins, ...filteredNewLogins];
      setAllLogins(updatedAllLogins);
      setLogins(updatedAllLogins);
      setLoginsPage(loginsPage + 1);
      // Check if backend returned full limit (not filtered count)
      setHasMoreLogins(rawNewLoginsCount >= filters.limit);
    } catch (err) {
      console.error('Error loading more logins:', err);
      setError(err.message);
    } finally {
      setLoadingMoreLogins(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [firebaseUser, environmentFilter, fetchStats]); // Re-fetch when environment filter changes

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    fetchStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading visit analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
        <div className="text-red-800">Error: {error}</div>
        <button 
          onClick={fetchStats}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Visit Analytics</h1>
        
        {/* Environment Filter Toggle */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Environment:</span>
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setEnvironmentFilter('all')}
              className={`px-4 py-2 text-sm font-medium border ${
                environmentFilter === 'all'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-l-lg`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setEnvironmentFilter('live')}
              className={`px-4 py-2 text-sm font-medium border-t border-b ${
                environmentFilter === 'live'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Live
            </button>
            <button
              type="button"
              onClick={() => setEnvironmentFilter('test')}
              className={`px-4 py-2 text-sm font-medium border ${
                environmentFilter === 'test'
                  ? 'bg-yellow-600 text-white border-yellow-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-r-lg`}
            >
              Test
            </button>
          </div>
          
          {/* Signup Filter - Only show on actions tab */}
          {activeTab === 'actions' && (
            <>
              <span className="text-sm font-medium text-gray-700 ml-4">View:</span>
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => setSignupFilter('all')}
                  className={`px-4 py-2 text-sm font-medium border ${
                    signupFilter === 'all'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } rounded-l-lg`}
                >
                  All Actions
                </button>
                <button
                  type="button"
                  onClick={() => setSignupFilter('signups-only')}
                  className={`px-4 py-2 text-sm font-medium border-t border-b ${
                    signupFilter === 'signups-only'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Signups Only
                </button>
                <button
                  type="button"
                  onClick={() => setSignupFilter('abandoned-only')}
                  className={`px-4 py-2 text-sm font-medium border ${
                    signupFilter === 'abandoned-only'
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } rounded-r-lg`}
                >
                  Abandoned Only
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Page</label>
            <input
              type="text"
              placeholder="e.g., /, /chat"
              value={filters.page}
              onChange={(e) => handleFilterChange('page', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={2000}>2000</option>
              <option value={5000}>5000</option>
              <option value={10000}>10000</option>
            </select>
          </div>
          {activeTab === 'actions' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
              <select
                value={filters.actionType}
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">All Actions</option>
                <option value="join_click">Join Clicks</option>
                <option value="signup_start">Signup Start</option>
                <option value="signup_step">Signup Steps</option>
                <option value="signup_complete">Signup Complete</option>
                <option value="signup_abandon">Signup Abandon</option>
              </select>
            </div>
          )}
          {activeTab === 'logins' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Login Method</label>
                <select
                  value={filters.loginMethod}
                  onChange={(e) => handleFilterChange('loginMethod', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">All Methods</option>
                  <option value="email">Email</option>
                  <option value="google">Google</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="text"
                  placeholder="Filter by user ID"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </>
          )}
        </div>
        <button
          onClick={applyFilters}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Apply Filters
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('visits')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'visits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Page Visits
            </button>
            <button
              onClick={() => setActiveTab('admin-visits')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'admin-visits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Admin Views
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'actions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Actions & Signup Funnel
            </button>
            <button
              onClick={() => setActiveTab('logins')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'logins'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Login Activity
            </button>
          </nav>
        </div>
      </div>

      {/* Statistics Overview */}
      {activeTab === 'visits' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Visits</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.totalVisits}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Authenticated Visits</h3>
            <p className="text-2xl font-bold text-green-600">{stats.authenticatedVisits}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Unique Users</h3>
            <p className="text-2xl font-bold text-blue-600">{stats.uniqueUsers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Most Popular Page</h3>
            <p className="text-lg font-semibold text-gray-900">
              {stats.pageViews && Object.keys(stats.pageViews).length > 0 
                ? Object.entries(stats.pageViews).sort((a, b) => b[1] - a[1])[0][0]
                : 'N/A'
              }
            </p>
          </div>
        </div>
      )}

      {/* Admin Visit Statistics */}
      {activeTab === 'admin-visits' && adminStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Admin Visits</h3>
            <p className="text-2xl font-bold text-gray-900">{adminStats.totalVisits}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Authenticated Admin Visits</h3>
            <p className="text-2xl font-bold text-green-600">{adminStats.authenticatedVisits}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Unique Admin Users</h3>
            <p className="text-2xl font-bold text-blue-600">{adminStats.uniqueUsers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Most Visited Admin Page</h3>
            <p className="text-lg font-semibold text-gray-900">
              {adminStats.pageViews && Object.keys(adminStats.pageViews).length > 0 
                ? Object.entries(adminStats.pageViews).sort((a, b) => b[1] - a[1])[0][0]
                : 'N/A'
              }
            </p>
          </div>
        </div>
      )}

      {/* Action Statistics */}
      {activeTab === 'actions' && actionStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Actions</h3>
            <p className="text-2xl font-bold text-gray-900">{actionStats.totalActions}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Join Clicks</h3>
            <p className="text-2xl font-bold text-blue-600">{actionStats.joinClicks}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Signup Starts</h3>
            <p className="text-2xl font-bold text-yellow-600">{actionStats.signupStarts}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Signup Completes</h3>
            <p className="text-2xl font-bold text-green-600">{actionStats.signupCompletes}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Signup Abandons</h3>
            <p className="text-2xl font-bold text-red-600">{actionStats.signupAbandons}</p>
          </div>
        </div>
      )}

      {/* Login Statistics */}
      {activeTab === 'logins' && loginStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Login Attempts</h3>
            <p className="text-2xl font-bold text-gray-900">{loginStats.totalLogins}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Successful Logins</h3>
            <p className="text-2xl font-bold text-green-600">{loginStats.successfulLogins}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Failed Logins</h3>
            <p className="text-2xl font-bold text-red-600">{loginStats.failedLogins}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Success Rate</h3>
            <p className="text-2xl font-bold text-blue-600">{loginStats.successRate}%</p>
          </div>
        </div>
      )}

      {/* Login Method Breakdown */}
      {activeTab === 'logins' && loginStats && loginStats.methodBreakdown && Object.keys(loginStats.methodBreakdown).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Login Methods</h2>
          <div className="space-y-2">
            {Object.entries(loginStats.methodBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([method, count]) => (
                <div key={method} className="flex justify-between items-center">
                  <span className="text-gray-700 capitalize">{method}</span>
                  <span className="font-semibold">{count} logins</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Login Device/Browser Breakdown */}
      {activeTab === 'logins' && loginStats && loginStats.deviceBreakdown && Object.keys(loginStats.deviceBreakdown).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold mb-4">Device Types</h2>
            <div className="space-y-2">
              {Object.entries(loginStats.deviceBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([device, count]) => (
                  <div key={device} className="flex justify-between items-center">
                    <span className="text-gray-700 capitalize">{device}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
            </div>
          </div>
          {loginStats.browserBreakdown && Object.keys(loginStats.browserBreakdown).length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold mb-4">Browsers</h2>
              <div className="space-y-2">
                {Object.entries(loginStats.browserBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([browser, count]) => (
                    <div key={browser} className="flex justify-between items-center">
                      <span className="text-gray-700">{browser}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click Breakdown */}
      {activeTab === 'actions' && actionStats && actionStats.clicksByButton && Object.keys(actionStats.clicksByButton).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Join Button Performance</h2>
          <div className="space-y-2">
            {Object.entries(actionStats.clicksByButton)
              .sort((a, b) => b[1] - a[1])
              .map(([button, count]) => (
                <div key={button} className="flex justify-between items-center">
                  <span className="text-gray-700">{button}</span>
                  <span className="font-semibold">{count} clicks</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Page Views Breakdown - Only for visits tab */}
      {activeTab === 'visits' && stats && stats.pageViews && Object.keys(stats.pageViews).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Page Views</h2>
          <div className="space-y-2">
            {Object.entries(stats.pageViews)
              .sort((a, b) => b[1] - a[1])
              .map(([page, count]) => (
                <div key={page} className="flex justify-between items-center">
                  <span className="text-gray-700">{page}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Admin Page Views Breakdown - Only for admin-visits tab */}
      {activeTab === 'admin-visits' && adminStats && adminStats.pageViews && Object.keys(adminStats.pageViews).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Admin Page Views</h2>
          <div className="space-y-2">
            {Object.entries(adminStats.pageViews)
              .sort((a, b) => b[1] - a[1])
              .map(([page, count]) => (
                <div key={page} className="flex justify-between items-center">
                  <span className="text-gray-700">{page}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {activeTab === 'visits' ? 'Recent Visits' : 
             activeTab === 'admin-visits' ? 'Recent Admin Visits' :
             activeTab === 'actions' ? 'Recent Actions' : 'Recent Login Activities'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                {(activeTab === 'visits' || activeTab === 'admin-visits') ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referrer
                    </th>
                  </>
                ) : activeTab === 'actions' ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device/Browser
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeTab === 'visits' ? (
                visits.map((visit) => (
                  <tr key={visit.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {visit.timestamp ? new Date(visit.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {visit.page || '/'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {visit.userId ? (
                        <div>
                          {visit.userEmail ? (
                            <>
                              <span className="text-gray-900 font-medium block">{visit.userEmail}</span>
                              <span className="text-xs text-gray-400">{visit.userId}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-green-600 font-medium block">Authenticated</span>
                              <span className="text-xs text-gray-500">{visit.userId}</span>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Anonymous</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {visit.ip || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                      {visit.referrer || 'Direct'}
                    </td>
                  </tr>
                ))
              ) : activeTab === 'admin-visits' ? (
                adminVisits.map((visit) => (
                  <tr key={visit.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {visit.timestamp ? new Date(visit.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {visit.page || '/'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {visit.userId ? (
                        <div>
                          {visit.userEmail ? (
                            <>
                              <span className="text-gray-900 font-medium block">{visit.userEmail}</span>
                              <span className="text-xs text-gray-400">{visit.userId}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-green-600 font-medium block">Authenticated</span>
                              <span className="text-xs text-gray-500">{visit.userId}</span>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Anonymous</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {visit.ip || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                      {visit.referrer || 'Direct'}
                    </td>
                  </tr>
                ))
              ) : activeTab === 'actions' ? (
                actions.map((action) => (
                  <tr key={action.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {action.timestamp ? new Date(action.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        action.actionType === 'join_click' ? 'bg-blue-100 text-blue-800' :
                        action.actionType === 'signup_start' ? 'bg-yellow-100 text-yellow-800' :
                        action.actionType === 'signup_complete' ? 'bg-green-100 text-green-800' :
                        action.actionType === 'signup_abandon' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {action.actionType.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {action.page || '/'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {action.userEmail && (
                        <div className="text-gray-700 font-medium">{action.userEmail}</div>
                      )}
                      {action.userId && (
                        <div className="text-xs text-gray-400">{action.userId}</div>
                      )}
                      {action.buttonText && (
                        <div>Button: {action.buttonText}</div>
                      )}
                      {action.signupMethod && (
                        <div>Method: {action.signupMethod}</div>
                      )}
                      {action.signupStep && (
                        <div>Step: {action.signupStep}</div>
                      )}
                      {/* Display abandoned signup information */}
                      {action.actionType === 'signup_abandon' && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                          <div className="font-semibold text-red-800 text-xs uppercase mb-1">Abandoned Signup Info:</div>
                          {action.abandonedEmail && (
                            <div className="text-gray-900 font-medium">
                              📧 {action.abandonedEmail}
                            </div>
                          )}
                          {action.abandonedDisplayName && (
                            <div className="text-gray-700">
                              👤 {action.abandonedDisplayName}
                            </div>
                          )}
                          {!action.abandonedEmail && !action.abandonedDisplayName && (
                            <div className="text-gray-500 text-xs">No user data captured</div>
                          )}
                        </div>
                      )}
                      {action.errorMessage && (
                        <div className="text-red-600 mt-1">Error: {action.errorMessage}</div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                logins.map((login) => (
                  <tr key={login.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {login.timestamp ? new Date(login.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        login.success === false ? 'bg-red-100 text-red-800' :
                        login.success === true ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {login.success === false ? 'Failed' : login.success === true ? 'Success' : 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="capitalize">{login.loginMethod || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {login.email ? (
                        <div>
                          <span className="text-gray-900 font-medium block">{login.email}</span>
                          <span className="text-xs text-gray-400">{login.userId}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">{login.userId || 'N/A'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div>{login.deviceType || 'Unknown'}</div>
                      <div className="text-xs text-gray-400">{login.browser || 'Unknown'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {login.ip && (
                        <div>IP: {login.ip}</div>
                      )}
                      {login.errorMessage && (
                        <div className="text-red-600">Error: {login.errorMessage}</div>
                      )}
                      {login.userAgent && (
                        <div className="text-xs text-gray-400 max-w-xs truncate">
                          UA: {login.userAgent}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Load More button for visits */}
          {activeTab === 'visits' && visits.length > 0 && hasMoreVisits && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={loadMoreVisits}
                disabled={loadingMoreVisits}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  loadingMoreVisits
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {loadingMoreVisits ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          
          {/* Load More button for admin visits */}
          {activeTab === 'admin-visits' && adminVisits.length > 0 && hasMoreAdminVisits && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={loadMoreAdminVisits}
                disabled={loadingMoreAdminVisits}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  loadingMoreAdminVisits
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {loadingMoreAdminVisits ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          
          {/* Load More button for actions */}
          {activeTab === 'actions' && actions.length > 0 && hasMoreActions && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={loadMoreActions}
                disabled={loadingMoreActions}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  loadingMoreActions
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {loadingMoreActions ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          
          {/* Load More button for logins */}
          {activeTab === 'logins' && logins.length > 0 && hasMoreLogins && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={loadMoreLogins}
                disabled={loadingMoreLogins}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  loadingMoreLogins
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {loadingMoreLogins ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          
          {((activeTab === 'visits' && visits.length === 0) || 
            (activeTab === 'admin-visits' && adminVisits.length === 0) ||
            (activeTab === 'actions' && actions.length === 0) ||
            (activeTab === 'logins' && logins.length === 0)) && (
            <div className="text-center py-8 text-gray-500">
              No {activeTab === 'visits' ? 'visits' : 
                   activeTab === 'admin-visits' ? 'admin visits' :
                   activeTab === 'actions' ? 'actions' : 'login activities'} found for the selected criteria
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitAnalytics;